// artifactRegressionCheck — detecterar när en AI-producerad extend
// "tappat" befintliga entiteter (blocks, connectors, groups).
//
// Operation-beteende:
//   extend         → regression = någon block/connector/group saknas (clean=false)
//   modify_in_place → regression tillåten (syfte), returnera bara info
//   refactor       → regression tillåten, men rapportera rename-counts
//   build_new      → regression-concept gäller inte (ingen parent)
//   revert         → inget — content matchar target-version exakt
//
// Matchning sker på BLOCK-id först (om AI bevarar id). Fallback-heuristik:
// blocks med samma `config.name` och `type` matchas som "renamed".

import type { WorkflowState, WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import type { Intent } from './intentTypes';

export interface RegressionResult {
  clean: boolean;
  operation: Intent;
  missingBlocks: Array<{ id: string; name: string; type: string }>;
  renamedBlocks: Array<{ id: string; oldName: string; newName: string }>;
  removedConnectors: number;
  removedGroups: number;
  summary: string;
}

/**
 * Kör regression-check mellan två WorkflowState-snapshots.
 *
 * För intent='extend' är clean=true bara om:
 *   1. Inga blocks saknas (varken id-match eller name+type match)
 *   2. Antal connectors är ≥ parent
 *   3. Antal groups är ≥ parent
 *
 * För andra operations är clean alltid true; vi rapporterar bara siffror.
 */
export function regressionCheck(
  previous: WorkflowState,
  next: WorkflowState,
  operation: Intent,
): RegressionResult {
  // build_new / revert / meta-* producerar inte regression
  if (operation === 'build_new' || operation.startsWith('meta.')) {
    return {
      clean: true,
      operation,
      missingBlocks: [],
      renamedBlocks: [],
      removedConnectors: 0,
      removedGroups: 0,
      summary: `${operation} — regression-check ej tillämplig`,
    };
  }

  const prevBlocks = new Map<string, WorkflowBlock>(
    previous.blocks.map((b) => [b.id, b]),
  );
  const nextBlocks = new Map<string, WorkflowBlock>(
    next.blocks.map((b) => [b.id, b]),
  );

  const nextByNameType = new Map<string, WorkflowBlock>();
  for (const b of next.blocks) {
    nextByNameType.set(blockKey(b), b);
  }

  const missing: RegressionResult['missingBlocks'] = [];
  const renamed: RegressionResult['renamedBlocks'] = [];

  for (const [id, pBlock] of prevBlocks) {
    if (nextBlocks.has(id)) continue; // exact id-match → bevarad
    // Försök matcha via name+type som "renamed"
    const fallback = nextByNameType.get(blockKey(pBlock));
    if (fallback) {
      renamed.push({
        id,
        oldName: pBlock.config?.name ?? '(anon)',
        newName: fallback.config?.name ?? '(anon)',
      });
    } else {
      missing.push({
        id,
        name: pBlock.config?.name ?? '(anon)',
        type: pBlock.type,
      });
    }
  }

  const removedConnectors = Math.max(0, previous.connectors.length - next.connectors.length);
  const removedGroups = Math.max(0, previous.groups.length - next.groups.length);

  // For extend, clean = no missing blocks AND no shrink i connectors/groups
  const cleanForExtend =
    operation === 'extend'
      ? missing.length === 0 && removedConnectors === 0 && removedGroups === 0
      : true;

  const summary = buildSummary(operation, missing.length, renamed.length, removedConnectors, removedGroups);

  return {
    clean: cleanForExtend,
    operation,
    missingBlocks: missing,
    renamedBlocks: renamed,
    removedConnectors,
    removedGroups,
    summary,
  };
}

function blockKey(b: WorkflowBlock): string {
  return `${b.type}:${b.config?.name ?? ''}`;
}

function buildSummary(
  operation: Intent,
  missingCount: number,
  renamedCount: number,
  removedConnectors: number,
  removedGroups: number,
): string {
  if (missingCount === 0 && renamedCount === 0 && removedConnectors === 0 && removedGroups === 0) {
    return `${operation}: inga regressioner — allt bevarat.`;
  }
  const parts: string[] = [];
  if (missingCount > 0) parts.push(`${missingCount} block saknas`);
  if (renamedCount > 0) parts.push(`${renamedCount} block döpta om`);
  if (removedConnectors > 0) parts.push(`${removedConnectors} connectors borta`);
  if (removedGroups > 0) parts.push(`${removedGroups} groups borta`);
  return `${operation}: ${parts.join(', ')}.`;
}

/**
 * Kompakt diff-representation för DB-lagring. Sparas i
 * artifact_version.diff_from_parent JSONB.
 */
export interface StructuralDiff {
  addedBlocks: Array<{ id: string; name: string; type: string }>;
  removedBlocks: Array<{ id: string; name: string; type: string }>;
  addedConnectors: number;
  removedConnectors: number;
  addedGroups: number;
  removedGroups: number;
}

export function computeStructuralDiff(
  previous: WorkflowState,
  next: WorkflowState,
): StructuralDiff {
  const prevIds = new Set(previous.blocks.map((b) => b.id));
  const nextIds = new Set(next.blocks.map((b) => b.id));

  const addedBlocks = next.blocks
    .filter((b) => !prevIds.has(b.id))
    .map((b) => ({ id: b.id, name: b.config?.name ?? '(anon)', type: b.type }));
  const removedBlocks = previous.blocks
    .filter((b) => !nextIds.has(b.id))
    .map((b) => ({ id: b.id, name: b.config?.name ?? '(anon)', type: b.type }));

  return {
    addedBlocks,
    removedBlocks,
    addedConnectors: Math.max(0, next.connectors.length - previous.connectors.length),
    removedConnectors: Math.max(0, previous.connectors.length - next.connectors.length),
    addedGroups: Math.max(0, next.groups.length - previous.groups.length),
    removedGroups: Math.max(0, previous.groups.length - next.groups.length),
  };
}

/**
 * Human-readable diff-text för ArtifactDiffView. Rader i + / - format.
 */
export function diffToLines(diff: StructuralDiff): string[] {
  const lines: string[] = [];
  for (const b of diff.addedBlocks) {
    lines.push(`+ Block '${b.name}' (${b.type}) tillagt`);
  }
  for (const b of diff.removedBlocks) {
    lines.push(`- Block '${b.name}' (${b.type}) borttaget`);
  }
  if (diff.addedConnectors > 0) lines.push(`+ ${diff.addedConnectors} connector(s) tillagda`);
  if (diff.removedConnectors > 0) lines.push(`- ${diff.removedConnectors} connector(s) borttagna`);
  if (diff.addedGroups > 0) lines.push(`+ ${diff.addedGroups} group(s) tillagda`);
  if (diff.removedGroups > 0) lines.push(`- ${diff.removedGroups} group(s) borttagna`);
  if (lines.length === 0) lines.push('(inga strukturella ändringar)');
  return lines;
}

// Re-exports för typ-konsistens
export type { Connector, BlockGroup };
